import { Button, Flex, Form, Input, Layout, message, Select, Typography, notification, ConfigProvider } from 'antd';
import api from 'api';
import auth from 'api/auth';
import { AxiosError } from 'axios';
import { languagesValues } from 'routes/Main/Main';
import { TFAuthModal } from 'components/Modals';
import { deleteCookie, saveTokensAfterAuthentication } from 'helpers/cookies';
import { ILoginData } from 'interfaces';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import ConfirmEmail from './confirmEmail/ConfirmEmail';
import classes from './Login.module.scss';
import LogoType from 'assets/img';
import { InfoCircleFilled } from '@ant-design/icons';
import { getBuildDefaultLanguage } from 'helpers/getBuildDefaultLanguage';
import VkButton from 'components/LoginButtons/VkButton/VkButton';
import YandexButton from 'components/LoginButtons/YandexButton/YandexButton';
import { useNotificationSound } from 'hooks/useNotificationSound/useNotificationSound';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';

const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME || 'tazadeal';

type LoginErrorType = {
  detail: string;
};

export default function Login() {
  const [messageApi, messageHolder ] = message?.useMessage();

  const [isLaoding, setIsLoading] = React.useState<boolean>(false);
  const [isOpenTfa, setIsOpenTfa] = React.useState<boolean>(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>('');
  const [form] = Form.useForm();

  const { enableSound } = useNotificationSound();
  
  const isMobile =
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      navigator.userAgent
    );
  
  const [searchParams] = useSearchParams();
  const [show, contextHolder] = notification.useNotification({top: ( window.innerHeight / 2 - 90)});
  const navigate = useNavigate();

  const defaultLanguage = localStorage.getItem('language') || getBuildDefaultLanguage();
  const emailToConfirm = searchParams.get('email');
  const needToOpenConfirm = searchParams.get('confirm_email');
  const prefill = searchParams.get('prefill');
  const ruSegmentQuickAuth = buildName === 'athena' || window.location.origin.includes('test') || window.location.origin.includes('localhost');

  const { i18n, t } = useTranslation();

  React.useEffect(() => {
    const isMobile = /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent)
    if (isMobile) navigate('/unsupported');

    if (!localStorage.getItem('language')) {
      const initialLanguage = getBuildDefaultLanguage();
      localStorage.setItem('language', initialLanguage);
      i18n.changeLanguage(initialLanguage);
    }
  }, []);

  React.useEffect(() => {
    needToOpenConfirm === 'true' && emailToConfirm && setIsOpenConfirmModal(true);
    // if (needToOpenConfirm === 'true' && emailToConfirm) {
    //   setIsOpenConfirmModal(true)
    // } else if (emailToConfirm) {
    //   form.setFieldsValue({ email: emailToConfirm });
    // }
    if (prefill === 'true' && emailToConfirm) {
      show.destroy(); // необходимо для корректной смены языка
      setTimeout(() => {
          show.info({
            message: t('userExist'),
            description: t('alreadyRegistered', {email: emailToConfirm}),
            placement: 'top',
            duration: 0,
            style: { padding: '24px 28px' },
            icon: <InfoCircleFilled className={classes.infoIcon} />,
          });
      }, 1);
      form.setFieldsValue({ email: emailToConfirm });
    };

  }, [i18n.language]);

  React.useEffect(() => {
    const allErrors: any[] = [];
    form.getFieldsError().forEach(item => allErrors.push(item.errors))
    allErrors.flat()[0] && form.validateFields()
  }, [i18n.language])

  const onCancelTfa = () => setIsOpenTfa(false);

  const loginHandle = async (values: ILoginData) => {
    setIsLoading(true);
    try {
      setEmail(values.email);
      deleteCookie('athena-token');
      deleteCookie('refresh_token');
      localStorage.removeItem('expired_refresh_token');

      const browserData = await functionCollectBrowserData();

      const formData = new FormData();
      formData.append('email', values.email.toLocaleLowerCase());
      formData.append(
        'details',
        JSON.stringify({
          email: values.email.toLocaleLowerCase(),
        })
      );
      formData.append('browser_data', JSON.stringify(browserData) || '');
      formData.append('password', values.password);

      const response = await auth.login(formData);

      if (response.data.message) {
        messageApi?.success(t('login.modals.verify.sendCode'));
        localStorage.setItem('tfaArgument', window.btoa(values.password))
        navigate(`/tfa/${values.email}`)
        return;
      }

      saveTokensAfterAuthentication(response.data);      
      messageApi?.destroy();

      const pendingParentInvite = sessionStorage.getItem('pending_parent_invite');
      if (pendingParentInvite) {
        try {
          const { parent_invite_id } = JSON.parse(pendingParentInvite);
        
          await api.acceptParentInvite({ parent_invite_id, email: values.email });
          sessionStorage.removeItem('pending_parent_invite');
          messageApi?.success(t('login.linkInviteAccepted'));
        } catch {
          sessionStorage.removeItem('pending_parent_invite');
          messageApi?.error(t('login.linkInviteError'));
        }
      }

      if (isMobile) navigate('/rooms');
      else navigate('/');

      if (localStorage.getItem('alreadyAllowSound') === 'true') {
        enableSound();
      }
      // browserData && await api.sendBrowserFingerprint(form_data);
    } catch (err) {      
      const error = err as AxiosError<LoginErrorType>;
      if (error.response?.data.detail === 'User is waiting for approval') {
        const systemName = buildName === 'tazadeal' ? 'TazaDeal' : 'Athena';
        messageApi?.warning(t(`login.waitingForApprove1`) + `'${systemName}'` + t(`login.waitingForApprove2`), 20);
        navigate(`/confirm-registration?email=${form.getFieldValue('email')}`);
      } else {
        messageApi?.error(error.response?.data.detail ? t(`login.${error.response?.data.detail}`) : t('login.someError'), 15);
      }

    } finally {
      setIsLoading(false);
    }
  };

  const onLanguageChange = (value: string) => {
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
  };

  return (
    <div className={classes.mainWrap}>
      <ConfigProvider
        theme={{
          components: {
            Notification: {
              width: 400
            },
          },
        }}
      >
        {messageHolder}
        {contextHolder}
      </ConfigProvider>
  
      <Layout className={classes.loginElem}>
        <Select
          className={classes.localizationSelect}
          defaultValue={defaultLanguage}
          onChange={onLanguageChange}
          style={{ minWidth: 130 }}
          options={languagesValues}
          optionLabelProp={'label'}
        />
        <div className={classes.loginCard}>
          <div className={classes.typography}>
            <img src={LogoType} className={classes.logo} alt='Logotype' />
            <Typography>
              <Typography.Title level={3}>{t('login.login')}</Typography.Title>
              <Typography.Paragraph className={classes.subTitle}>
                {t('login.havenAcc')}{' '}
                <Typography.Link href='/signup' className={classes.primary}>
                  {t('login.signup')}
                </Typography.Link>
              </Typography.Paragraph>
            </Typography>
          </div>
          <Form form={form} layout='vertical' className={classes.loginForm} onFinish={loginHandle}>
            <Form.Item
              name={'email'}
              label={t('login.email')}
              rules={[{ required: true, message: t('login.inputEmail'), type: 'email' }]}
            >
              <Input
                placeholder={t('login.email')}
                className={classes.input}
              />
            </Form.Item>
            <Form.Item
              name={'password'}
              label={t('login.password')}
              rules={[{ required: true, message: t('login.inputPassword') }]}
            >
              <Input.Password
                placeholder={t('login.password')}
                className={classes.input}
              />
            </Form.Item>

            {/* <Checkbox>{t('login.keep')}</Checkbox> */}
            <Button type='primary' htmlType='submit' loading={isLaoding}>
              {t('login.logIn')}
            </Button>
          </Form>

          {ruSegmentQuickAuth &&
            <>
              <div className={classes.orGroupBtns}>{t('login.external.btns.orUsing')}</div>
              <Flex gap="middle" className={classes.socialBtns}>
                <YandexButton action={'auth'}/>
                <VkButton action={'auth'}/>
              </Flex>
            </>
          }

          <Typography>
            <Flex className={classes.textWrap} gap='middle'>
              <div>
                {t('login.forgot')}{' '}
                <Typography.Link href='/reset' className={classes.primary}>
                  {t('resetPassword.reset')}
                </Typography.Link>
              </div>
            </Flex>
          </Typography>
        </div>
      </Layout>


      {isOpenConfirmModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmEmail
            isOpen={isOpenConfirmModal}
            onCancel={() => setIsOpenConfirmModal(false)}
            email={emailToConfirm}
          />
        </React.Suspense>
      )}

      {isOpenTfa && (
        <React.Suspense fallback={<div />}>
          <TFAuthModal email={email} isOpen t={t} onCancel={onCancelTfa} />
        </React.Suspense>
      )}
    </div>
  );
}
